import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate  } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { PROFILE, TRANSPORT } from "helpers/routes";

import TransportDetails from "components/TransportComponents/TransportDetails";
import CompanyDocuments from "components/common/InfoPopup/CompanyDocuments";
import CompanyData from "components/common/InfoPopup/CompanyData";
import ContentContainer from "components/common/InfoPopup/ContentContainer";
import HeaderContainer from "components/common/InfoPopup/HeaderContainer";
import Navigation from "components/common/InfoPopup/Navigation";
import CompanyReviews from "components/common/InfoPopup/CompanyReviews";
import TooltipButton from "components/common/InfoPopup/TooltipButton";
import DeleteModal from "components/PopupForms/DeleteDialog/DeleteModal";
import { ICON } from "components/common/IconButton";

import PropTypes from "prop-types";

import styles from "./styles.module.scss";
import Button from 'components/common/Button';
import { useMedia } from 'hooks/useMedia';

const DETAIL_TRANSPORT_MENU = {
    ABOUT: "about",
    DETAIL: "detail",
    CONTACTS: "contacts",
    DOCUMENTS: "documents",
    NOTES: "notes",
    REVIEW: "review",
};

const TransportInfo = ({ transport, onClosePopup, deleteTransport, isOpen }) => {
    const [currentSection, setCurrentSection] = useState(DETAIL_TRANSPORT_MENU.DETAIL);
    const currentUserId = useSelector((state) => state.profile.profileData.id);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isDesktop } = useMedia();

    const componentToRender = {
        [DETAIL_TRANSPORT_MENU.ABOUT]: <CompanyData companyData={transport.user} />,
        [DETAIL_TRANSPORT_MENU.DETAIL]: <TransportDetails transport={transport} />,
        [DETAIL_TRANSPORT_MENU.DOCUMENTS]: (
            <CompanyDocuments documentsData={transport.transport_technical_documents} />
        ),
        [DETAIL_TRANSPORT_MENU.REVIEW]: (
            <CompanyReviews companyId={transport.user && transport.user.company && transport.user.company.id} />
        ),
    };

    const navigationItems = [
        {
            id: DETAIL_TRANSPORT_MENU.DETAIL,
            active: DETAIL_TRANSPORT_MENU.DETAIL === currentSection,
            content: t("exchangeModal.learn_more_section.heading"),
        },
        {
            id: DETAIL_TRANSPORT_MENU.ABOUT,
            active: DETAIL_TRANSPORT_MENU.ABOUT === currentSection,
            content: t("exchangeModal.contact_section.heading"),
        },
        {
            id: DETAIL_TRANSPORT_MENU.REVIEW,
            active: DETAIL_TRANSPORT_MENU.REVIEW === currentSection,
            content: t("exchangeModal.review_section.heading"),
        },
    ];
    if (
        transport.transport_technical_documents &&
        transport.transport_technical_documents.length > 0
    ) {
        navigationItems.push({
            id: DETAIL_TRANSPORT_MENU.DOCUMENTS,
            active: DETAIL_TRANSPORT_MENU.DOCUMENTS === currentSection,
            content: t("exchangeModal.document_section.heading"),
        });
    }

    const handleMenuClick = (id) => () => {
        setCurrentSection(id);
    };

    const isMyTransport = currentUserId === transport.user_id;

    const handleEditClick = () => {
        navigate(`${PROFILE.editTransport}?id=${transport.id}`);
    };

    const handleDelete = (id, callback) => {
        deleteTransport(id);
        callback();
    };

    const handleSendToExchange = () => {
        navigate(`${TRANSPORT.addToExchange}?transport=${transport.id}`);
    };

    const popupButtons = isMyTransport && (
                    <div className={styles.buttons_container}>
                      <div className={styles.buttons_container__tooltip_container}>
                        <p>{t("edit")}</p>
                        <TooltipButton
                            onClick={handleEditClick}
                            hintText={t("edit")}
                            icon={ICON.edit}
                        />
                        </div>
                      <div className={styles.buttons_container__tooltip_container}>
                        <p>{t("transport.delete")}</p>
                        <DeleteModal
                            onDeleteClick={handleDelete}
                            data={{
                                title: t("transport.delete"),
                                id: transport.id,
                                name: t("transport.transport"),
                            }}
                        />
                        </div>
                    </div>
                )

    return (
        <ContentContainer
            onClosePopup={onClosePopup}
            isOpen={isOpen}
            popupButtons={isDesktop && popupButtons}
        >
            {transport && Object.keys(transport).length && (
                <HeaderContainer
                    showChatButton={transport.use_chat}
                    companyName={
                        transport.user && transport.user.company && transport.user.company.name
                    }
                    companyId={transport.user && transport.user.company && transport.user.company.id}
                    isVerified={Boolean(transport.user?.company?.is_verified)}
                    companyLogo={
                        transport.user && transport.user.picture && transport.user.picture.data
                    }
                    items={[
                        {
                            title: t("transportFields.main_section.country"),
                            value: transport.country_id.label,
                        },
                        {
                            title: t("transportFields.main_section.license"),
                            value: transport.registration_number,
                        },
                        {
                            title: t("transportFields.main_section.name"),
                            value: transport.own_name && transport.own_name,
                        },
                        // Сводка из макета: вид, тип и грузоподъёмность сразу в шапке
                        {
                            title: t("park.vehicle_menu_headings.view"),
                            value: transport.transport_bodytype_additional?.value,
                        },
                        {
                            title: t("park.vehicle_menu_headings.type"),
                            value:
                                transport.transport_bodytypes?.length > 0
                                    ? transport.transport_bodytypes.map((el) => el.value).join(", ")
                                    : transport.transport_bodytype?.value,
                        },
                        {
                            title: t("park.vehicle_menu_headings.capacity"),
                            value:
                                parseFloat(transport.carrying_capacity) >= 0
                                    ? `${transport.carrying_capacity} ${t("units.tons")}`
                                    : null,
                        },
                        {
                            title: t("park.vehicle_menu_headings.monitoring"),
                            value: t(transport.satellite_monitoring ? "yes" : "no"),
                        },
                    ]}
                >
                    <Button className={styles.button} onClick={handleSendToExchange}>
                        {t("transportFields.main_section.send_exchange")}
                    </Button>
                </HeaderContainer>
            )}
            {!isDesktop && popupButtons}
            <Navigation onClick={handleMenuClick} items={navigationItems} />
            {transport && Object.keys(transport).length && componentToRender[currentSection]}
        </ContentContainer>
    );
};

TransportInfo.propTypes = {
    transport: PropTypes.shape({
        id: PropTypes.number,
        user_id: PropTypes.number,
        use_chat: PropTypes.bool,
        own_name: PropTypes.string,
        registration_number: PropTypes.string,
        country_id: PropTypes.shape({ label: PropTypes.string }),
        user: PropTypes.shape({
            company: PropTypes.shape({ name: PropTypes.string, id: PropTypes.number }),
            picture: PropTypes.shape({ data: PropTypes.any }),
        }),
        transport_technical_documents: PropTypes.array,
    }),
    onClosePopup: PropTypes.func,
    deleteTransport: PropTypes.func,
    isOpen: PropTypes.bool,
};

export default TransportInfo;
